import { Modal, ModalBody, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { useSnackbar } from '@components/snackbar';
import { ShoppingCartIcon } from '@sketchnote/icons';
import { frontendHostUrl } from 'config';
import { useRouter } from 'next/router';
import React from 'react';
import { API } from 'src/api';
import { useAuth } from 'src/shared/hooks/use-auth';
import { useLayout } from 'src/shared/hooks/use-layout';

import { CsvImportSelectFile } from './components/csv-import/csv-import';
import { CsvFileMap } from './components/csv-import/csv-map';
import { LmsInstructorWrapper } from './components/lmsInstructorWrapper';
import { PrivatePinnedData } from './components/private';
import { SharedPinnedData } from './components/shared';
import { SketchnoteLessons } from './components/sketchnoteLessons';
import { StartNewFile } from './components/start-new-file/start-new-file';
import { useSidebarStore } from './store';
const { useEffect, useState } = React;

export interface LmsWorkspaces {
  id: string;
  name: string;
  isOpen?: boolean;
  isCustom?: boolean;
  members: any;
  owner: any;
  access?: string;
  // isPopupOpen: boolean;
  workspaceMenuItems?: { label: string | JSX.Element; value: string; icon?: JSX.Element }[];
}

export interface LmsSideBarProps {
  isOpen: boolean;
  menuItems?: {
    label: string | JSX.Element;
    value: string;
    icon?: JSX.Element;
    href?: string;
  }[];
  hasWorkspaceAccess?: boolean;
  handleToggleSideBar?: ((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void) | undefined;
  catalogHref?: string;
  isOverview?: boolean;
}

export const LmsSideBar = ({
  isOpen,
  handleToggleSideBar,
  isOverview = false,
}: LmsSideBarProps): JSX.Element => {
  const router = useRouter();

  const { onNewFileOpen, onNewFileClose, isNewFileOpen } = useLayout();
  const { fetchSidebar } = useSidebarStore();
  const [isCsvImportModalOpen, setIsCsvImportModalOpen] = useState(false);
  const [isProductTourModalOpen, setIsProductTourOpen] = useState(false);
  const [isCsvMapModalOpen, setIsCsvMapModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File>();
  const [fileTitle, setFileTitle] = useState('');
  const [workspace, setWorkspace] = useState<{ label: string; value: string | undefined }>({
    label: '',
    value: '',
  });

  const [openSnackbar] = useSnackbar();

  const authData = useAuth((s) => s.userDetails);
  const mode = router?.query?.mode;

  const memberRole = authData?.memberInformation?.role;
  const fetchUserDetails = useAuth((s) => s.fetchUserDetails);

  useEffect(() => {
    if (router.query.mode) {
      fetchSidebar(mode);
    }
  }, [router.query.mode]);

  const handleCloseProductTourModal = async () => {
    const payload = {
      type: 'files',
      value: false,
    };
    setIsProductTourOpen(false);
    await API.put('/settings/product-tour', payload);
    fetchUserDetails();
    onNewFileOpen();
  };

  const onShowCsvMap = (file, fileTitle, workspace) => {
    if (!file[0]) {
      setImportFile(undefined);
      openSnackbar({
        text: 'An unexpected error occurred while uploading the file',
        snackType: 'error',
        position: 'bottom-center',
        duration: 2000,
      });
    } else {
      setImportFile(file);
      setIsCsvMapModalOpen(true);
      setIsCsvImportModalOpen(false);
      setFileTitle(fileTitle);
      setWorkspace(workspace);
    }
  };

  return (
    <LmsInstructorWrapper isOverview={isOverview} isOpen={isOpen} onToggle={handleToggleSideBar}>
      <>
        {frontendHostUrl !== 'https://app.sketchnote.co/' && (
          <div className="overflow-auto h-full px-14 no-scrollbar">
            {authData?.authentication === 'authorised' && (
              <div>
                <SketchnoteLessons />
              </div>
            )}
          </div>
        )}

        <Modal isOpen={isProductTourModalOpen} onClose={handleCloseProductTourModal}>
          <ModalOverlay />
          <ModalContent
            style={{
              maxWidth: '50vw',
              padding: '32px',
              marginTop: '6rem',
              borderRadius: '8px',
            }}
          >
            <ModalCloseButton />
            <video
              playsInline
              loop
              controls
              preload="auto"
              width="100%"
              className="shadow-mdl2 rounded-md"
            >
              <source
                type="video/mp4"
                src="https://res.cloudinary.com/samvibes/video/upload/academy/fileview.mp4"
              />
              <source
                type="video/webm"
                src="https://res.cloudinary.com/samvibes/video/upload/academy/fileview.mp4"
              />
              Sorry, your browser doesn’t support embedded videos.
            </video>

            <div className="mt-24 w-full">
              <div className="flex flex-col justify-center mx-auto">
                <p className="text-blue-800 font-semibold text-xl ">
                  Your projects’ new home!
                  <span role="img" aria-label="partying face">
                    &nbsp;📁
                  </span>
                </p>
              </div>
              <p className="text-sm text-gray-600">
                The brand new file system hosts all your work—from docs to kanban boards and more.
              </p>
            </div>
          </ModalContent>
        </Modal>

        <Modal isOpen={isNewFileOpen} onClose={onNewFileClose}>
          <ModalOverlay />
          <ModalContent style={{ maxWidth: '750px', borderRadius: '8px' }}>
            <ModalCloseButton />
            <StartNewFile onClose={onNewFileClose} />
          </ModalContent>
        </Modal>

        {/* CSV import modal start */}
        <Modal isOpen={isCsvImportModalOpen} onClose={() => setIsCsvImportModalOpen(false)}>
          <ModalOverlay />
          <ModalContent
            style={{
              maxWidth: '46.4rem',
              borderRadius: '0.8rem',
              margin: 'auto',
              backgroundColor: 'white',
              padding: '32px',
            }}
          >
            <ModalCloseButton className="text-xs text-gray-500 m-24" />
            <ModalBody>
              <CsvImportSelectFile
                onClose={() => {
                  return;
                }}
                showCsvMap={onShowCsvMap}
              />
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* CSV map modal open */}
        {importFile && importFile[0]?.name ? (
          <Modal isOpen={isCsvMapModalOpen} onClose={() => setIsCsvMapModalOpen(false)}>
            <ModalOverlay />
            <ModalCloseButton />
            <ModalContent
              maxHeight="auto"
              className="static rounded-lg"
              style={{ maxWidth: '102.4rem', borderRadius: '8px' }}
            >
              <CsvFileMap
                file={importFile}
                fileTitle={fileTitle}
                workspace={workspace.value ? workspace.value : undefined}
                onClose={() => setIsCsvMapModalOpen(false)}
              />
            </ModalContent>
          </Modal>
        ) : (
          <></>
        )}
      </>
    </LmsInstructorWrapper>
  );
};
